import React, { useState, useEffect } from "react";
import {
  ModelCost,
  CostItem,
  Workshop,
  Model,
  Firm,
  CalculationType,
} from "../types";
import {
  costService,
  modelService,
  firmService,
  workshopService,
} from "../services/dataService";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { useExchangeRates } from "../context/ExchangeRateContext";
import AlertModal from "./AlertModal";
import ConfirmModal from "./ConfirmModal";
import "./OrderCostsModal.css";

interface OrderCostsModalProps {
  isOpen: boolean;
  orderId: string;
  modelId: string;
  onClose: () => void;
}

const OrderCostsModal: React.FC<OrderCostsModalProps> = ({
  isOpen,
  orderId,
  modelId,
  onClose,
}) => {
  const { usdRate, eurRate, gbpRate } = useExchangeRates();

  // State
  const [model, setModel] = useState<Model | null>(null);
  const [firm, setFirm] = useState<Firm | null>(null);
  const [modelCosts, setModelCosts] = useState<ModelCost[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ModelCost | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    if (isOpen && orderId && modelId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId, modelId]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [allModels, allFirms, costs, items, workshopList] =
        await Promise.all([
          modelService.getAll(),
          firmService.getFirms(),
          costService.getModelCosts(orderId),
          costService.getCostItems(),
          workshopService.getAll(),
        ]);

      const foundModel = allModels.find((m) => m.modelId === modelId);
      setModel(foundModel || null);

      if (foundModel?.firmId) {
        const foundFirm = allFirms.find((f) => f.firmId === foundModel.firmId);
        setFirm(foundFirm || null);
      }

      setModelCosts(costs);
      setCostItems(items);
      setWorkshops(workshopList);
    } catch (error) {
      console.error("❌ Failed to load order costs:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet verileri yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Döviz dönüşümü fonksiyonu
  const convertToTRY = (
    amount: number,
    currency: string,
    exchangeRates: { usdRate?: number; eurRate?: number; gbpRate?: number },
  ): number => {
    if (currency === "TRY") return amount;

    const { usdRate, eurRate, gbpRate } = exchangeRates;

    switch (currency) {
      case "USD":
        if (!usdRate) return 0;
        return amount * usdRate;
      case "EUR":
        if (!eurRate) return 0;
        return amount * eurRate;
      case "GBP":
        if (!gbpRate) return 0;
        return amount * gbpRate;
      default:
        return amount;
    }
  };

  // Toplam maliyeti TL'ye çevirerek hesapla
  const calculateTotalCost = (): number => {
    return modelCosts.reduce((sum, item) => {
      const itemTotal = item.totalCost || 0;
      const tryAmount = convertToTRY(itemTotal, item.currency || "TRY", {
        usdRate: item.usdRate,
        eurRate: item.eurRate,
        gbpRate: item.gbpRate,
      });
      return sum + tryAmount;
    }, 0);
  };

  const handleDelete = async (item: ModelCost) => {
    setConfirmModal({
      isOpen: true,
      message: `"${item.costItemName || "Bu"}" maliyet kalemini kaldırmak istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await costService.deleteModelCost(item.modelCostId);
          setAlertModal({
            isOpen: true,
            message: "Maliyet başarıyla kaldırıldı!",
            type: "success",
          });
          loadData();
        } catch (error) {
          console.error("❌ Failed to delete model cost:", error);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kaldırılamadı!",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleEdit = (item: ModelCost) => {
    const costItem = costItems.find((ci) => ci.costItemId === item.costItemId);
    const enrichedItem = {
      ...item,
      quantity1Label: item.quantity1Label || costItem?.quantity1Label,
      quantity2Label: item.quantity2Label || costItem?.quantity2Label,
      calculationType: item.calculationType || costItem?.calculationType,
    };
    setEditingItem(enrichedItem);
    setShowEditModal(true);
  };

  const handleUpdateModelCost = async (updatedItem: ModelCost) => {
    try {
      setLoading(true);

      // CalculationType CustomCost (6/Fason) ise quantity 0 gönder, totalCost kullanılır
      const updateData = { ...updatedItem };
      if (updatedItem.calculationType === CalculationType.CustomCost) {
        updateData.quantity = 0;
      }

      await costService.updateModelCost(updatedItem.modelCostId, updateData);
      setAlertModal({
        isOpen: true,
        message: "Maliyet başarıyla güncellendi!",
        type: "success",
      });
      setShowEditModal(false);
      setEditingItem(null);
      loadData();
    } catch (error) {
      console.error("❌ Failed to update model cost:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet güncellenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddModelCost = async (newCost: Partial<ModelCost>) => {
    try {
      setLoading(true);

      // CalculationType CustomCost için quantity 0 gönder, totalCost kullanılır
      const modelCostData = {
        ...newCost,
        modelId,
        orderId,
        usdRate: usdRate || undefined,
        eurRate: eurRate || undefined,
        gbpRate: gbpRate || undefined,
        exchangeRateDate: new Date().toISOString(),
        isActive: true,
      };

      // CalculationType CustomCost (6/Fason) ise quantity 0 olarak gönder
      if (newCost.calculationType === CalculationType.CustomCost) {
        modelCostData.quantity = 0;
      }

      await costService.addModelCost(
        modelCostData as Omit<ModelCost, "modelCostId">,
      );
      setAlertModal({
        isOpen: true,
        message: "Maliyet başarıyla eklendi!",
        type: "success",
      });
      setShowAddModal(false);
      loadData();
    } catch (error: any) {
      console.error("❌ Failed to add model cost:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet eklenemedi: " + (error.message || "Bilinmeyen hata"),
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const firstCost = modelCosts[0];
  const orderQuantity = firstCost?.orderQuantity || 0;
  const totalCost = calculateTotalCost();
  const unitCost = orderQuantity > 0 ? totalCost / orderQuantity : 0;

  return (
    <div className="order-costs-modal-overlay">
      <div className="order-costs-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="order-costs-modal__header">
          <div className="order-costs-modal__header-info">
            <h2>Sipariş Maliyetleri</h2>
            <div className="order-costs-modal__breadcrumb">
              {firm && <span>{firm.firmName}</span>}
              {firm && model && <span className="separator">›</span>}
              {model && <span>{model.modelCode || model.modelName}</span>}
            </div>
          </div>
          <button className="order-costs-modal__close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Summary */}
        <div className="order-costs-modal__summary">
          <div className="order-costs-modal__summary-item">
            <span className="label">Toplam Maliyet</span>
            <span className="value">{formatCurrency(totalCost, "TRY")}</span>
          </div>
          {orderQuantity > 0 && (
            <>
              <div className="order-costs-modal__summary-item">
                <span className="label">Sipariş Adedi</span>
                <span className="value">{orderQuantity} Adet</span>
              </div>
              <div className="order-costs-modal__summary-item">
                <span className="label">Birim Maliyet</span>
                <span className="value">{formatCurrency(unitCost, "TRY")}</span>
              </div>
            </>
          )}
          <div className="order-costs-modal__summary-item">
            <span className="label">Kalem Sayısı</span>
            <span className="value">{modelCosts.length}</span>
          </div>
          {firstCost?.exchangeRateDate && (
            <div className="order-costs-modal__summary-item">
              <span className="label">Kur Tarihi</span>
              <span className="value">
                {new Date(firstCost.exchangeRateDate).toLocaleDateString(
                  "tr-TR",
                )}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="order-costs-modal__content">
          {loading ? (
            <div className="order-costs-modal__loading">
              <div className="spinner"></div>
              <p>Yükleniyor...</p>
            </div>
          ) : modelCosts.length === 0 ? (
            <div className="order-costs-modal__empty">
              <p>Bu sipariş için henüz maliyet kaydı bulunmuyor.</p>
            </div>
          ) : (
            <div className="order-costs-modal__table-wrapper">
              <table className="order-costs-modal__table">
                <thead>
                  <tr>
                    <th>Maliyet Kalemi</th>
                    <th>Kategori</th>
                    <th>Birim 1</th>
                    <th className="center">Miktar 1</th>
                    <th>Birim 2</th>
                    <th className="center">Miktar 2</th>
                    <th className="center">Fire %</th>
                    <th className="center">Net Miktar</th>
                    <th className="right">Birim Fiyat</th>
                    <th className="right">Toplam</th>
                    <th className="right">TL Karşılığı</th>
                    <th>Kullanım</th>
                    <th className="center">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {modelCosts.map((item) => {
                    const tryEquivalent = convertToTRY(
                      item.totalCost || 0,
                      item.currency || "TRY",
                      {
                        usdRate: item.usdRate,
                        eurRate: item.eurRate,
                        gbpRate: item.gbpRate,
                      },
                    );

                    return (
                      <tr key={item.modelCostId}>
                        <td>
                          <strong>{item.costItemName || "-"}</strong>
                        </td>
                        <td>
                          <span className="category-badge">
                            {item.costCategoryName || "-"}
                          </span>
                        </td>
                        <td>{item.unit || "-"}</td>
                        <td className="center">
                          <strong>{item.quantity}</strong>
                        </td>
                        <td>
                          {item.unit2 ? (
                            <span>{item.unit2}</span>
                          ) : (
                            <span className="muted">-</span>
                          )}
                        </td>
                        <td className="center">
                          {item.quantity2 ? (
                            <strong>{item.quantity2}</strong>
                          ) : (
                            <span className="muted">-</span>
                          )}
                        </td>
                        <td className="center">
                          {item.wastagePercentage !== undefined &&
                          item.wastagePercentage !== null ? (
                            <span className="wastage">
                              %{item.wastagePercentage}
                            </span>
                          ) : (
                            <span className="muted">-</span>
                          )}
                        </td>
                        <td className="center">
                          {item.actualQuantityNeeded ? (
                            <strong className="net-qty">
                              {formatNumber(item.actualQuantityNeeded)}
                            </strong>
                          ) : (
                            item.quantity
                          )}
                        </td>
                        <td className="right">
                          {item.unitPrice &&
                            formatCurrency(item.unitPrice, item.currency)}
                        </td>
                        <td className="right">
                          <strong className="total-cost">
                            {item.totalCost &&
                              formatCurrency(item.totalCost, item.currency)}
                          </strong>
                        </td>
                        <td className="right">
                          <strong className="try-cost">
                            {formatCurrency(tryEquivalent, "TRY")}
                          </strong>
                          {item.currency !== "TRY" && item.usdRate && (
                            <div className="exchange-info">
                              1 {item.currency} ={" "}
                              {item.currency === "USD"
                                ? item.usdRate
                                : item.currency === "EUR"
                                  ? item.eurRate
                                  : item.gbpRate}{" "}
                              TL
                            </div>
                          )}
                        </td>
                        <td>
                          {item.order?.workshop?.name ? (
                            <span className="workshop-badge">
                              {item.order.workshop.name}
                            </span>
                          ) : item.usage ? (
                            <span className="usage" title={item.usage}>
                              {item.usage.substring(0, 20)}
                              {item.usage.length > 20 ? "..." : ""}
                            </span>
                          ) : (
                            <span className="muted">-</span>
                          )}
                        </td>
                        <td className="center">
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleEdit(item)}
                              title="Düzenle"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              className="btn btn-sm btn-soft-danger"
                              onClick={() => handleDelete(item)}
                              title="Sil"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="order-costs-modal__footer">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowAddModal(true)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Yeni Maliyet Ekle
          </button>
          <button
            className="order-costs-modal__btn order-costs-modal__btn--close"
            onClick={onClose}
          >
            Kapat
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <EditCostModal
          item={editingItem}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          onSave={handleUpdateModelCost}
        />
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddCostModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddModelCost}
          costItems={costItems}
          workshops={workshops}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

// Edit Cost Modal Component
interface EditCostModalProps {
  item: ModelCost;
  onClose: () => void;
  onSave: (item: ModelCost) => void;
}

const EditCostModal: React.FC<EditCostModalProps> = ({
  item,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<ModelCost>(item);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field: keyof ModelCost, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="edit-cost-modal-overlay">
      <div className="edit-cost-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-cost-modal__header">
          <h3>Miktar Düzenle</h3>
          <button className="edit-cost-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="edit-cost-modal__body">
            {/* Maliyet Kalemi Bilgisi */}
            <div className="edit-cost-modal__info">
              <div className="info-row">
                <span className="label">Maliyet Kalemi:</span>
                <span className="value">{formData.costItemName}</span>
              </div>
              <div className="info-row">
                <span className="label">Kategori:</span>
                <span className="value">{formData.costCategoryName}</span>
              </div>
              <div className="info-row">
                <span className="label">Birim Fiyat:</span>
                <span className="value">
                  {formData.unitPrice} {formData.currency}
                </span>
              </div>
            </div>

            <div className="edit-cost-modal__fields">
              {/* Quantity 1 */}
              <div className="field-group">
                <label>{formData.quantity1Label || "Miktar"} *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.quantity || ""}
                  onChange={(e) =>
                    handleChange("quantity", parseFloat(e.target.value) || 0)
                  }
                  required
                />
              </div>
              <div className="field-group">
                <label>Birim</label>
                <input
                  type="text"
                  value={formData.unit || ""}
                  readOnly
                  disabled
                />
              </div>

              {/* Quantity 2 */}
              {formData.quantity2 !== undefined &&
                formData.quantity2 !== null && (
                  <>
                    <div className="field-group">
                      <label>{formData.quantity2Label || "Miktar 2"}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.quantity2 || ""}
                        onChange={(e) =>
                          handleChange(
                            "quantity2",
                            parseFloat(e.target.value) || undefined,
                          )
                        }
                      />
                    </div>
                    <div className="field-group">
                      <label>Birim 2</label>
                      <input
                        type="text"
                        value={formData.unit2 || ""}
                        readOnly
                        disabled
                      />
                    </div>
                  </>
                )}
            </div>
          </div>

          <div className="edit-cost-modal__footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              İptal
            </button>
            <button type="submit" className="btn btn-primary">
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Cost Modal Component
interface AddCostModalProps {
  onClose: () => void;
  onSave: (cost: Partial<ModelCost>) => void;
  costItems: CostItem[];
  workshops: Workshop[];
}

interface ModelCostFormData extends Partial<ModelCost> {
  quantity1Label?: string;
  quantity2Label?: string;
}

const AddCostModal: React.FC<AddCostModalProps> = ({
  onClose,
  onSave,
  costItems,
  workshops,
}) => {
  const [formData, setFormData] = useState<ModelCostFormData>({
    costItemId: "",
    quantity: 0,
    unit: "",
    quantity2: undefined,
    unit2: "",
    unitPrice: 0,
    totalCost: 0,
    currency: "TRY",
    usage: "",
    notes: "",
    wastagePercentage: 0,
    quantity1Label: "",
    quantity2Label: "",
    calculationType: undefined,
  });

  const [, setSelectedCostItem] = useState<CostItem | null>(null);
  const [selectedWorkshopId, setSelectedWorkshopId] = useState<string>("");
  const [workshopCostItems, setWorkshopCostItems] = useState<CostItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const handleWorkshopChange = async (workshopId: string) => {
    setSelectedWorkshopId(workshopId);
    const selectedWorkshop = workshops.find((w) => w.workshopId === workshopId);

    setFormData((prev) => ({
      ...prev,
      usage: selectedWorkshop?.name || "",
      costItemId: "",
    }));

    if (workshopId) {
      try {
        setLoadingItems(true);
        const workshopItems =
          await costService.getWorkshopCostItems(workshopId);

        const items: CostItem[] = workshopItems
          .filter((wi) => wi.costItemId)
          .map((wi) => ({
            costItemId: wi.costItemId,
            costCategoryId: "",
            itemName: wi.costItemName || "",
            description: wi.costItemDescription,
            unit: wi.unitCode,
            unitName: wi.quantity1Label,
            unit2: wi.unitCode2,
            unitPrice: wi.effectivePrice || wi.standardPrice || 0,
            currency: wi.effectiveCurrency || wi.standardCurrency || "TRY",
            supplier: undefined,
            wastagePercentage: undefined,
            minimumOrderQuantity: undefined,
            discountPercentage: wi.workshopDiscountPercentage || undefined,
            lastPriceUpdate: new Date().toISOString(),
            isActive: true,
            calculationType: wi.calculationType,
            materialWidth: wi.materialWidth,
            materialHeight: wi.materialHeight,
            quantity1Label: wi.quantity1Label,
            quantity2Label: wi.quantity2Label,
            quantity3Label: wi.quantity3Label,
            costCategory: {
              costCategoryId: "",
              category: wi.categoryName || "",
              categoryName: wi.categoryName || "",
              description: undefined,
              displayOrder: undefined,
              isActive: true,
            },
          }));

        setWorkshopCostItems(items);
      } catch (error) {
        console.error("❌ Atölye maliyet kalemleri yüklenemedi:", error);
        setWorkshopCostItems([]);
      } finally {
        setLoadingItems(false);
      }
    } else {
      setWorkshopCostItems([]);
    }
  };

  const handleCostItemChange = (costItemId: string) => {
    const item = workshopCostItems.find((i) => i.costItemId === costItemId);
    setSelectedCostItem(item || null);

    setFormData((prev) => ({
      ...prev,
      costItemId,
      unitPrice: item?.unitPrice || 0,
      currency: item?.currency || "TRY",
      unit: item?.unitName || item?.unit || "",
      unit2: item?.unit2 || "",
      quantity1Label: item?.quantity1Label,
      quantity2Label: item?.quantity2Label,
      calculationType: item?.calculationType,
    }));
  };

  const calculateTotal = () => {
    // CustomCost için totalCost direkt girilir, hesaplanmaz
    if (formData.calculationType === CalculationType.CustomCost) {
      return;
    }

    const qty1 = formData.quantity || 0;
    // Boya hesaplamada Çeşit Sayısı (quantity2) yalnız bilgi amaçlı — çarpana girmez
    const isPaintBased =
      formData.calculationType === CalculationType.PaintBased;
    const qty2 = isPaintBased ? 1 : formData.quantity2 || 1;
    const price = formData.unitPrice || 0;
    const wastage = formData.wastagePercentage || 0;

    let total = qty1 * qty2 * price;
    if (wastage > 0) {
      total = total * (1 + wastage / 100);
    }

    setFormData((prev) => ({ ...prev, totalCost: total }));
  };

  useEffect(() => {
    calculateTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.quantity,
    formData.quantity2,
    formData.unitPrice,
    formData.wastagePercentage,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedWorkshopId) {
      return;
    }

    if (!formData.costItemId) {
      return;
    }

    // CustomCost için totalCost validasyonu
    if (formData.calculationType === CalculationType.CustomCost) {
      if (!formData.totalCost || formData.totalCost <= 0) {
        return;
      }
    } else {
      // Diğer tipler için quantity validasyonu
      if (!formData.quantity || formData.quantity <= 0) {
        return;
      }
    }

    const { quantity1Label, quantity2Label, ...modelCostData } = formData;
    onSave(modelCostData);
  };

  return (
    <div className="add-cost-modal-overlay" onClick={onClose}>
      <div className="add-cost-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-cost-modal__header">
          <h3>Yeni Maliyet Ekle</h3>
          <button className="add-cost-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="add-cost-modal__body">
            {/* Atölye Seçimi */}
            <div className="field-group full-width">
              <label>Atölye *</label>
              <select
                value={selectedWorkshopId}
                onChange={(e) => handleWorkshopChange(e.target.value)}
                required
              >
                <option value="">Önce atölye seçiniz...</option>
                {workshops.map((workshop) => (
                  <option key={workshop.workshopId} value={workshop.workshopId}>
                    {workshop.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Maliyet Kalemi Seçimi */}
            <div className="field-group full-width">
              <label>Maliyet Kalemi *</label>
              <select
                value={formData.costItemId || ""}
                onChange={(e) => handleCostItemChange(e.target.value)}
                required
                disabled={!selectedWorkshopId || loadingItems}
              >
                <option value="">
                  {loadingItems
                    ? "Yükleniyor..."
                    : !selectedWorkshopId
                      ? "Önce atölye seçiniz"
                      : workshopCostItems.length === 0
                        ? "Bu atölyede tanımlı maliyet yok"
                        : "Seçiniz..."}
                </option>
                {workshopCostItems.map((item) => (
                  <option key={item.costItemId} value={item.costItemId}>
                    {item.itemName} - {item.costCategory?.categoryName} (
                    {formatCurrency(item.unitPrice, item.currency)})
                  </option>
                ))}
              </select>
            </div>

            {/* Miktar 1 - CustomCost hariç */}
            {formData.calculationType !== CalculationType.CustomCost && (
              <div className="field-group">
                <label>
                  {formData.quantity1Label || "Miktar"} (
                  {formData.unit || "Birim"}) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.quantity || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      quantity: parseFloat(e.target.value) || 0,
                    }))
                  }
                  required
                  placeholder="Miktar girin"
                />
              </div>
            )}

            {/* Miktar 2 */}
            {formData.quantity2Label &&
              formData.calculationType !== CalculationType.CustomCost && (
                <div className="field-group">
                  <label>
                    {formData.quantity2Label}{" "}
                    {formData.unit2 && `(${formData.unit2})`}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantity2 || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        quantity2: parseFloat(e.target.value) || undefined,
                      }))
                    }
                    placeholder="Miktar girin"
                  />
                </div>
              )}

            {/* Toplam Tutar */}
            <div className="field-group full-width">
              <label>
                Toplam Tutar
                {formData.calculationType === CalculationType.CustomCost &&
                  " *"}
              </label>
              {formData.calculationType === CalculationType.CustomCost ? (
                <input
                  type="number"
                  step="0.01"
                  value={formData.totalCost || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      totalCost: parseFloat(e.target.value) || 0,
                    }))
                  }
                  required
                  placeholder="Toplam tutar girin"
                  style={{
                    padding: "0.625rem",
                    fontSize: "0.875rem",
                    border: "1px solid #ddd",
                    borderRadius: "0.25rem",
                  }}
                />
              ) : (
                <div className="total-display">
                  {formatCurrency(
                    formData.totalCost || 0,
                    formData.currency || "TRY",
                  )}
                </div>
              )}
            </div>

            {/* Notlar */}
            <div className="field-group full-width">
              <label>Notlar</label>
              <textarea
                value={formData.notes || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                rows={3}
                placeholder="İsteğe bağlı notlar..."
              />
            </div>
          </div>

          <div className="add-cost-modal__footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              İptal
            </button>
            <button type="submit" className="btn btn-primary">
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderCostsModal;
